import BadgeIcon from './BadgeIcon';
import React, { useState, useRef } from 'react';
import { AnimatePresence, motion, useSpring, useMotionTemplate, transform } from 'motion/react';
import { useLayer, useHover, Arrow } from 'react-laag';
import { FaGift } from 'react-icons/fa6';
import './NeoBadge.css';
import './SnappyInfo.css';

function SnappyInfo({ onInteractionChange, snappyDetails }) {
  const [isOpen, setOpen] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);

  const { renderLayer, triggerProps, layerProps } = useLayer({
    isOpen,
    placement: 'bottom-center',
    auto: true,
    triggerOffset: 8, // Distance between trigger and layer
    containerOffset: 16, // Minimum distance to container edges
    arrowOffset: 8 // Distance between layer and arrow
  });

  React.useEffect(() => {
    if (onInteractionChange) {
      onInteractionChange(isOpen || isHovering);
    }
  }, [isOpen, isHovering, onInteractionChange]);

  const handleMouseEvents = (e) => {
    e.stopPropagation();
  };
  if (layerProps?.style) {
	layerProps.style.zIndex = 9999;
  }

  return (
    <>
      <button
        className="snappy-button"
        {...triggerProps}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!isOpen);
        }}
        onMouseEnter={(e) => {
          handleMouseEvents(e);
          setIsHovering(true);
        }}
        onMouseMove={handleMouseEvents}
        onMouseLeave={(e) => {
          handleMouseEvents(e);
          setIsHovering(false);
        }}
      >
        <FaGift
          color="#ffffff"
          size={20}
          aria-label="Snappy Rewards"
        />
      </button>
      {renderLayer(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              {...layerProps}
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
              className="snappy-popup"
			 // style={{zIndex: 9999}}
              onClick={handleMouseEvents}
              onMouseEnter={(e) => {
                handleMouseEvents(e);
                setIsHovering(true);
              }}
              onMouseMove={handleMouseEvents}
              onMouseLeave={(e) => {
                handleMouseEvents(e);
                setIsHovering(false);
              }}
            >
              <h4 className="snappy-popup-header">{snappyDetails?.snappyGrantName || 'Snappy Reward'}</h4>
              <p className="snappy-popup-amount">{snappyDetails?.costInCurrency || '$0'}</p>
              <p className="snappy-popup-description">{snappyDetails?.description || 'Snappy Gift Attached ⭐️'}</p>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </>
  );
}

const NeoBadge = ({ hideSnappyIcon = true, snappyDetails = {}, shape = 'hexagon', icon = "ANCHOR", colors = {gradient: "linear-gradient(to bottom right, #6366f1, #0ea5e9, #10b981)", icon: "#ffffff", text: "#ffffff" }, text, captureId, useTilt = true }) => {
	const [isHovered, setIsHovered] = useState(false);
	const [snappyInteracting, setSnappyInteracting] = useState(false);
	const [frame, setFrame] = useState({
		width: 0,
		height: 0,
		top: 0,
		left: 0
	})
	const badgeRef = useRef(null);

	const rotateValue = 10;
  	const transformValue = rotateValue * 2;
	const transformValue2 = rotateValue * 1.5;
  	const springValue = { stiffness: 400, damping: 30 };

	const rotateX = useSpring(0, springValue);
	const rotateY = useSpring(0, springValue);
	const x = useSpring(0, springValue);
	const y = useSpring(0, springValue);
	const x2 = useSpring(0, springValue);
	const y2 = useSpring(0, springValue);
	const shadowX = useSpring(0, springValue);
	const shadowY = useSpring(30, springValue);
	const shadow2X = useSpring(0, springValue);
	const shadow2Y = useSpring(10, springValue);

	const filter = useMotionTemplate`drop-shadow(${shadowX}px ${shadowY}px 20px rgba(0, 0, 68, 0.25))`;
	const filter2 = useMotionTemplate`drop-shadow(${shadow2X}px ${shadow2Y}px 20px rgba(0, 0, 68, 0.25))`;

	const convertCursorPosition = e => {
		const objectX = (e.nativeEvent.clientX - frame.left) / frame.width;
		const objectY = (e.nativeEvent.clientY - frame.top) / frame.height;

		rotateX.set(transform(objectY, [0, 1], [rotateValue, -rotateValue]));
		rotateY.set(transform(objectX, [0, 1], [-rotateValue, rotateValue]));
		x.set(transform(objectX, [0, 1], [-transformValue, transformValue]));
		y.set(transform(objectY, [0, 1], [-transformValue, transformValue]));
		x2.set(transform(objectX, [0,1], [-transformValue2, transformValue2]));

		shadowX.set(transform(objectX, [0, 1], [20, -20]));
		shadowY.set(transform(objectY, [0, 1], [60, 20]));
		shadow2X.set(transform(objectX, [0, 1], [20, -20]));
		shadow2Y.set(transform(objectY, [0, 1], [40, 20]));

  	};

	/* On Mouse Enter, get object frame and convert values */
	 	const handleMouseEnter = e => {
		// Skip if Snappy component is being interacted with
		if (snappyInteracting) return;
		
		const currentElement = e.target.getBoundingClientRect();
		if (e.target._meaningfulType == 'IMAGE') return;

		setFrame({
			width: currentElement.width,
			height: currentElement.height,
			top: currentElement.top,
			left: currentElement.left
		});
		setIsHovered(true);

		convertCursorPosition(e);
	};

	/* On Mouse Move, convert values */
	const handleMouseMove = e => {
		// Skip if Snappy component is being interacted with
		if (snappyInteracting) return;
		
		if (e.target._meaningfulType == 'IMAGE') return;
		convertCursorPosition(e);
	};

	/* On Mouse Leave, reset */
	const handleMouseLeave = e => {
		rotateX.set(0);
		rotateY.set(0);
		x.set(0);
		y.set(0);
		shadowX.set(0);
		shadowY.set(40);
		shadow2X.set(0);
		shadow2Y.set(20);
		setIsHovered(false);
	};

	// React-laag popover for Snappy icon
	const [hoverProps, isSnappyHovered] = useHover();


	const getBadgeShapeClass = () => {
		switch (shape) {
			case 'circle':
				return 'neo-badge-circle-shape';
			case 'hexagon':
				return 'neo-badge-hexagon-shape';
			case 'shield':
				return 'neo-badge-shield-shape';
			case 'flower':
				return 'neo-badge-flower-shape';
			case 'wavy':
				return 'neo-badge-wavy-shape';
			case 'starburst':
				return 'neo-badge-starburst-shape';
			default:
				return 'neo-badge-square-shape';
		}
	};

	return (
		<motion.div
			id={captureId?.toString() ? captureId?.toString() : null}
			ref={badgeRef}
			className="neo-badge-container"
			onMouseMove={handleMouseMove}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			style={{
				rotateX: (useTilt && !snappyInteracting) ? rotateX : 0,
				rotateY: (useTilt && !snappyInteracting) ? rotateY : 0,
				transformStyle: "preserve-3d",
				perspective: '1200px'
				//scale: captureId?.toString() ? 1 : .65,
			}}
			animate={{
				scale: 1,
			}}
			transition={{
				scale: { type: "spring", stiffness: 300, damping: 30 }
			}}
		>
			{/* Layer 1: Badge Shape - Base Layer */}
			<motion.div
				className={`neo-badge-shape ${getBadgeShapeClass()}`}
				style={{
					background: colors.gradient,
					backfaceVisibility: "hidden",
					WebkitBackfaceVisibility: "hidden",
					zIndex: 1,
					width: 260,
					height: 260,
					placeItems: "center",
					placeContent: "center",
				}}
				animate={{
					translateZ: 1,
				}}
				transition={{ type: "spring", stiffness: 400, damping: 30 }}
			>
			{/* Layer 2: White Circle - Separate from badge shape */}
			<motion.div
				className="neo-badge-circle"
				style={{
					backfaceVisibility: "hidden",
					WebkitBackfaceVisibility: "hidden",
					zIndex: 2,
					filter: filter2,
					// background: 'linear-gradient(45deg, rgb(99, 102, 241), rgb(67, 56, 202))' // background gradient example for badge circle
					// backgroundColor: '#fee32fd', // example changing color 
					// opacity: 0, // example to hide it
				}}
				animate={{
					translateZ: 3,
				}}
				transition={{ type: "spring", stiffness: 400, damping: 30 }}
			>
				
			</motion.div>
			</motion.div>
			<motion.div
					initial={{filter: `drop-shadow(1px 1px 1px ${colors.icon})`}}
					style={{
						backfaceVisibility: "hidden",
						WebkitBackfaceVisibility: "hidden",
						zIndex: 3,
						alignSelf: 'center',
						position: 'relative',
						gridArea: '1/-1',
						justifySelf: 'center'
						//filter
					}}
					animate={{
						translateZ: 20,
						ilter: isHovered ? `drop-shadow(4px 4px 2px rgba(81, 81, 81, 0.25))` : `drop-shadow(1px 1px 1px rgba(81, 81, 81, 0.25)`
					}}
					transition={{ type: "spring", stiffness: 400, damping: 10 }}
				>
					<BadgeIcon iconName={icon} size={64} className="neo-badge-icon" color={colors.icon} /> {/* here you can change icon size and color! */}
				</motion.div>
			{/* Layer 4: Label - Separate from badge shape */}
			<motion.div
				className="neo-badge-label"
				style={{
					backfaceVisibility: "hidden",
					WebkitBackfaceVisibility: "hidden",
					alignSelf: 'self-end',
					zIndex: 5,
					x,
					y,
					filter,
					overflow: 'visible'
				}}
				animate={{
					translateZ: 5,
				}}
				transition={{ type: "spring", stiffness: 400, damping: 30 }}
			>
				<span className="neo-badge-text" style={{ color: colors.text }}>{text}</span>
			</motion.div>

			
			
			

			{/* Layer 5: Snappy Icon - Top right corner */}
			{(() => {
				
				return !hideSnappyIcon && snappyDetails?.snappyGrantId && (
				<motion.div
					className="neo-badge-snappy-icon"
					style={{
						gridArea: '1/-1',
						alignSelf: 'self-start',
						justifySelf: 'end',
						textAlign: 'end',
						zIndex: 10,
						backfaceVisibility: "hidden",
						WebkitBackfaceVisibility: "hidden",
						pointerEvents: 'auto' // Ensure it can receive pointer events
					}}
					animate={{
						translateZ: 40, // Keep it above other elements
					}}
					transition={{ type: "spring", stiffness: 400, damping: 30 }}
					onClick={(e) => e.stopPropagation()} // Stop click propagation
				>
					<SnappyInfo snappyDetails={snappyDetails} onInteractionChange={setSnappyInteracting} />
				</motion.div>
			)})()}
		</motion.div>
	);
};

export default NeoBadge;
